if (!customElements.get('wi-product-overview')) {
  customElements.define('wi-product-overview', class extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      const options = { signal: this._controller.signal };
      const details = this.querySelector(':scope > details');
      const video = this.querySelector('video');
      if (!details || !video) return;
      const viewer = this.querySelector('dialog');
      const stage = this.querySelector('[data-wi-video-stage]');
      const viewerButton = this.querySelector('[data-wi-video-fullscreen]');
      const fallback = this.querySelector('[data-wi-video-viewer-fallback]');
      const viewerReady = Boolean(viewer && stage && typeof viewer.showModal === 'function');
      const captionPanes = Array.from(this.querySelectorAll('[data-wi-video-caption-pane]'));
      const clearCaptionDisplay = () => captionPanes.forEach(pane => {
        pane.hidden = true;
        const text = pane.querySelector('[data-wi-video-caption-text]');
        if (text) text.textContent = '';
      });
      this._clearCaptionDisplay = clearCaptionDisplay;
      const errors = Array.from(this.querySelectorAll('[data-wi-video-error]'));
      this._failed = Boolean(video.error) || errors.some(error => !error.hidden);
      const showError = () => {
        this._failed = true;
        clearCaptionDisplay();
        if (fallback) fallback.hidden = false;
        errors.forEach(error => { error.hidden = false; });
        if (viewer?.open && stage) stage.hidden = true;
      };
      const clearErrors = () => {
        this._failed = false;
        errors.forEach(error => { error.hidden = true; });
        if (stage) stage.hidden = false;
      };
      video.addEventListener('error', showError, options);
      video.querySelector('source')?.addEventListener('error', showError, options);
      video.addEventListener('loadedmetadata', () => {
        clearErrors();
        if (fallback) fallback.hidden = viewerReady;
      }, options);
      let captionElement = video.querySelector('track[kind="captions"][srclang="en"]');
      let captionTrack = captionElement?.track;
      const captionButtons = Array.from(this.querySelectorAll('[data-wi-video-captions]'));
      const captionRetryButtons = Array.from(this.querySelectorAll('[data-wi-video-caption-retry]'));
      const captionFailures = Array.from(this.querySelectorAll('[data-wi-video-caption-error]'));
      const transcript = this.querySelector('[data-wi-video-transcript]');
      this._captionsOn ??= true;
      let captionsOpened = false;
      let expectedCaptionMode = null;
      let webkitFullscreen = false;
      let pictureInPicture = false;
      const usesNativeCaptionSurface = () => {
        const element = document.fullscreenElement || document.webkitFullscreenElement;
        return pictureInPicture || document.pictureInPictureElement === video || webkitFullscreen || video.webkitDisplayingFullscreen === true || Boolean(element && (element === video || element.contains(video)));
      };
      const renderCaptions = () => {
        const loaded = captionElement?.readyState === 2 && video.readyState >= 1;
        const show = this.isConnected && this._captionsOn && captionsOpened && loaded && !captionFailed && !this._failed && !usesNativeCaptionSurface() && (viewer?.open || details.open);
        let text = '';
        if (show && !video.ended) {
          text = Array.from(captionTrack?.activeCues || []).map(cue => {
            try { return typeof cue.getCueAsHTML === 'function' ? cue.getCueAsHTML().textContent : cue.text; }
            catch { return cue.text; }
          }).filter(value => typeof value === 'string').join('\n').trim();
        }
        captionPanes.forEach(pane => {
          const active = show && (pane.dataset.wiVideoCaptionPane === 'viewer' ? viewer?.open : !viewer?.open);
          pane.hidden = !active;
          const target = pane.querySelector('[data-wi-video-caption-text]');
          if (target && target.textContent !== (active ? text : '')) target.textContent = active ? text : '';
        });
      };
      let captionFailed = Boolean(captionElement?.hasAttribute('src') && captionElement.readyState === 3);
      const syncCaptionControls = () => {
        const requested = captionElement?.hasAttribute('src');
        const loaded = captionElement?.readyState === 2;
        const failed = captionsOpened && (!captionTrack || captionFailed);
        captionButtons.forEach(button => {
          button.hidden = !requested || !captionTrack;
          button.disabled = !loaded || failed;
          button.setAttribute('aria-pressed', String(Boolean(loaded && !failed && this._captionsOn)));
          const state = button.querySelector('[data-wi-video-caption-state]');
          if (state) state.textContent = failed ? 'Unavailable' : loaded ? (this._captionsOn ? 'On' : 'Off') : 'Loading…';
        });
        captionFailures.forEach(message => { message.hidden = !failed; });
        captionRetryButtons.forEach(button => { button.hidden = !captionTrack; });
      };
      const applyCaptionPreference = () => {
        if (captionTrack && captionElement.hasAttribute('src') && !captionFailed) {
          // Keep the VTT loaded for the separate strip, including when toggled Off.
          // Actual system fullscreen retains native captions and their controls.
          const mode = this._captionsOn && usesNativeCaptionSurface() ? 'showing' : 'hidden';
          expectedCaptionMode = mode;
          if (captionTrack.mode !== mode) captionTrack.mode = mode;
        }
        syncCaptionControls();
        renderCaptions();
      };
      const prepareCaptionSource = () => {
        captionsOpened = true;
        if (captionElement?.dataset.wiVideoCaptionSrc && !captionElement.hasAttribute('src')) {
          captionFailed = false;
          captionElement.src = captionElement.dataset.wiVideoCaptionSrc;
          captionElement.default = true;
        }
        applyCaptionPreference();
      };
      const retryCaptionSource = () => {
        if (!captionsOpened || !captionElement || !captionFailed) return;
        const source = captionElement.dataset.wiVideoCaptionSrc;
        if (!source) return;
        const replacement = captionElement.cloneNode(false);
        replacement.removeAttribute('src');
        replacement.removeAttribute('default');
        captionElement.replaceWith(replacement);
        captionElement = replacement;
        captionTrack = replacement.track;
        captionFailed = false;
        bindCaptionElement(replacement);
        replacement.src = source;
        replacement.default = true;
        applyCaptionPreference();
      };
      const focusTranscript = () => {
        if (!transcript) return;
        transcript.open = true;
        transcript.querySelector('summary')?.focus();
      };
      captionButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (button.disabled || !captionTrack) return;
          this._captionsOn = !this._captionsOn;
          applyCaptionPreference();
        }, options);
      });
      const bindCaptionElement = element => {
        element?.track?.addEventListener('cuechange', () => {
          if (element === captionElement) renderCaptions();
        }, options);
        element?.addEventListener('load', () => {
          if (element !== captionElement || !element.hasAttribute('src')) return;
          captionFailed = false;
          applyCaptionPreference();
        }, options);
        element?.addEventListener('error', () => {
          if (element !== captionElement || !element.hasAttribute('src')) return;
          captionFailed = true;
          if (captionTrack) captionTrack.mode = 'disabled';
          syncCaptionControls();
          clearCaptionDisplay();
        }, options);
      };
      bindCaptionElement(captionElement);
      video.textTracks?.addEventListener('change', () => {
        if (!captionTrack || captionFailed || captionElement.readyState !== 2) return;
        // Programmatic hidden mode is our custom On presentation, not user Off.
        if (captionTrack.mode !== expectedCaptionMode) {
          this._captionsOn = captionTrack.mode === 'showing';
          applyCaptionPreference();
        } else {
          syncCaptionControls();
          renderCaptions();
        }
      }, options);
      video.addEventListener('loadedmetadata', applyCaptionPreference, options);
      ['timeupdate', 'seeked', 'loadeddata', 'ended', 'emptied'].forEach(event => video.addEventListener(event, renderCaptions, options));
      document.addEventListener('fullscreenchange', applyCaptionPreference, options);
      document.addEventListener('webkitfullscreenchange', applyCaptionPreference, options);
      video.addEventListener('webkitbeginfullscreen', () => { webkitFullscreen = true; applyCaptionPreference(); }, options);
      video.addEventListener('webkitendfullscreen', () => { webkitFullscreen = false; applyCaptionPreference(); }, options);
      video.addEventListener('enterpictureinpicture', () => { pictureInPicture = true; applyCaptionPreference(); }, options);
      video.addEventListener('leavepictureinpicture', () => { pictureInPicture = false; applyCaptionPreference(); }, options);
      this.querySelectorAll('[data-wi-video-caption-transcript]').forEach(button => {
        button.addEventListener('click', () => {
          video.pause();
          if (viewer?.open) {
            this._showTranscriptAfterClose = true;
            viewer.close();
          } else {
            focusTranscript();
          }
        }, options);
      });
      captionRetryButtons.forEach(button => {
        button.addEventListener('click', retryCaptionSource, options);
      });
      syncCaptionControls();
      renderCaptions();
      this.querySelectorAll('[data-wi-video-retry]').forEach(button => {
        button.addEventListener('click', () => { clearErrors(); video.load(); retryCaptionSource(); applyCaptionPreference(); }, options);
      });
      this._closeViewer = () => {
        video.pause();
        (this.querySelector('[data-wi-video-caption-pane="inline"]') || viewerButton)?.before(video);
        if (this._previousOverflow !== undefined) document.body.style.overflow = this._previousOverflow;
        this._previousOverflow = undefined;
        renderCaptions();
        if (viewerButton?.isConnected) viewerButton.focus({ preventScroll: true });
        if (this._showTranscriptAfterClose) {
          this._showTranscriptAfterClose = false;
          focusTranscript();
        }
      };
      viewer?.addEventListener('close', () => { if (!viewer.open) this._closeViewer(); }, options);
      this.querySelector('[data-wi-video-close]')?.addEventListener('click', () => { video.pause(); viewer.close(); }, options);
      viewer?.addEventListener('cancel', () => video.pause(), options);
      viewerButton?.addEventListener('click', () => {
        if (!viewer || viewer.open) return;
        try {
          if (typeof viewer.showModal !== 'function') throw new Error('Viewer unavailable');
          if (this._previousOverflow === undefined) this._previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          viewer.querySelector('[data-wi-video-stage]').append(video);
          viewer.showModal();
          applyCaptionPreference();
          if (this._failed) showError();
        } catch {
          this._closeViewer();
          if (fallback) fallback.hidden = false;
        }
      }, options);
      const prepareMedia = () => {
        if (!details.open) { video.pause(); renderCaptions(); return; }
        prepareCaptionSource();
        const source = video.querySelector('source[data-wi-video-src]');
        if (source && !source.hasAttribute('src')) {
          source.src = source.dataset.wiVideoSrc;
          video.load();
          applyCaptionPreference();
        }
      };
      details.addEventListener('toggle', prepareMedia, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) video.pause();
      }, options);
      // Keep the real file reachable until an actual media load succeeds.
      if (fallback) fallback.hidden = viewerReady && !this._failed && video.readyState >= 1;
      if (viewerButton) viewerButton.hidden = !viewerReady;
      if (details.open) prepareMedia();
    }
    disconnectedCallback() {
      const fallback = this.querySelector('[data-wi-video-viewer-fallback]');
      const viewerButton = this.querySelector('[data-wi-video-fullscreen]');
      if (fallback) fallback.hidden = false;
      if (viewerButton) viewerButton.hidden = true;
      this._clearCaptionDisplay?.(); this._closeViewer?.(); this.querySelector('dialog[open]')?.close(); this._controller?.abort(); this._controller = null; this._closeViewer = null; this._clearCaptionDisplay = null; }
  });
}
